import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, createVNode as _createVNode, createCommentVNode as _createCommentVNode, unref as _unref, createBlock as _createBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "relative" }
const _hoisted_2 = ["innerHTML"]
const _hoisted_3 = ["innerHTML"]
const _hoisted_4 = {
  key: 0,
  class: "flex flex-col md:flex-row gap-6 md:gap-4 mt-2 self-stretch"
}
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { key: 1 }
const _hoisted_7 = {
  key: 1,
  class: "w-full py-[0.5rem]"
}

import { onMounted, ref } from 'vue';
import { InsurancePolicy, PolicyHolder } from '@/interfaces';
import { InformationBoxTypes } from '@/interfaces/forms/information-box.interface';
import { SelfServiceService } from '@/services/store/self-service-service';
import { replaceBookmark } from '@/utils/bookmark.replace';
import { format as formatDate } from 'date-fns';
import { getFormInput } from '@/utils/form-input-helper';
import SimpleReviewSummary from '@/components/ReviewSummary/SimpleReviewSummary.vue';
import ReviewSummaryItem from '@/components/ReviewSummary/ReviewSummaryItem.vue';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import DocumentLink from '@/components/DocumentLink/DocumentLink.vue';
import { PolicyStatus } from '@/enums/policy-status.enum';

enum Bookmarks {
  policyStatus = 'policyStatus',
  policyNumber = 'policyNumber',
  policyholders = 'policyholders',
  certificateCurrencyLink = 'certificateCurrencyLink',
  certificateInsuranceLink = 'certificateInsuranceLink',
  policyPeriod = 'policyPeriod',
  insuranceTypeDisplayName = 'insuranceTypeDisplayName',
  vehicleDetails = 'vehicleDetails',
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PolicySummary',
  props: {
    currentPolicy: {},
    content: {}
  },
  setup(__props: any) {

const props = __props;

const policySummary = ref<any>();
const certificateDownloadResponse = ref<any>();
const certificateOfInsuranceDownloadError = ref<any>(
  getFormInput(props.content, 'certificateOfInsuranceDownloadError')
);
const certificateOfCurrencyDownloadError = ref<any>(getFormInput(props.content, 'certificateOfCurrencyDownloadError'));

onMounted(async () => {
  policySummary.value = getPolicySummaryModel();
});

function getPolicySummaryModel(): any {
  const tempPolicySummary = getFormInput(props.content, 'policySummary');
  if (!tempPolicySummary) return { title: '', icon: '', items: [] };
  return {
    title: tempPolicySummary.title,
    icon: tempPolicySummary.fields[0].icon,
    items: tempPolicySummary.fields[0].items,
  };
}

async function downloadDocument(documentStorageId: string) {
  certificateDownloadResponse.value = '';
  try {
    await SelfServiceService.getDownloadDocument(documentStorageId);
  } catch (error) {
    console.error('err', error);
    certificateDownloadResponse.value = 'coi-error';
  }
}

//Sitecore 8 uses request Coc for Certificate of Currency
async function requestCoc(policyNumber: string) {
  certificateDownloadResponse.value = '';

  try {
    await SelfServiceService.requestCoCDocument(policyNumber);
  } catch (error) {
    console.error('coc-err', error);
    certificateDownloadResponse.value = 'coc-error';
  }
}

const getPolicyStatusComponent = (content: any) => {
  if (!props.currentPolicy || !props.currentPolicy?.status) return content;

  const statusIcons = [
    {
      status: PolicyStatus.ACTIVE,
      icon: 'bg-icon-active',
      displayStatus: PolicyStatus.ACTIVE,
    },
    {
      status: PolicyStatus.IN_RENEWAL,
      icon: 'bg-icon-active',
      displayStatus: PolicyStatus.ACTIVE,
    },
    {
      status: PolicyStatus.CANCELLED,
      icon: 'bg-icon-cancelled',
      displayStatus: PolicyStatus.CANCELLED,
    },
    {
      status: PolicyStatus.OVERDUE,
      icon: 'bg-icon-cancelled',
      displayStatus: PolicyStatus.OVERDUE,
    },
  ];

  let useStatusDetails = statusIcons.find((x) => x.status === props.currentPolicy?.status);

  return (
    content.value,
    `<div class="flex">
    <span class="${useStatusDetails?.icon} h-[10px] w-[11px] self-center">&nbsp;</span>
    <span class="pl-2">${useStatusDetails?.displayStatus}</span>
    </div>`
  );
};

const getPolicyholders = () => {
  if (!props.currentPolicy?.policyHolders) return '';

  const policyholders = props.currentPolicy?.policyHolders
    .map((policyholder: PolicyHolder, index) => {
      const isPrimary = index === 0 && props.currentPolicy?.policyHolders.length > 1 ? '(Primary)' : '';
      return `<div class='py-0.5 my-0'>${policyholder.firstName} ${policyholder.lastName} ${isPrimary}</div>`;
    })
    .join('');

  return policyholders;
};

function fillBookmarks(content: Field<string>) {
  if (!props.currentPolicy || !content?.value) return content;

  /* Policy summary */
  content.value = replaceBookmark(Bookmarks.policyStatus, content.value, getPolicyStatusComponent(content));
  content.value = replaceBookmark(Bookmarks.policyNumber, content.value, props.currentPolicy.policyNumber);
  content.value = replaceBookmark(Bookmarks.policyholders, content.value, getPolicyholders());

  if (props.currentPolicy?.termStart && props.currentPolicy?.termEnd) {
    content.value = replaceBookmark(
      Bookmarks.policyPeriod,
      content.value,
      `${formatDate(props.currentPolicy.termStart, 'dd MMMM yyyy')} - ${formatDate(props.currentPolicy.termEnd, 'dd MMMM yyyy')}`
    );
  }

  /* Policy details */
  content.value = replaceBookmark(Bookmarks.vehicleDetails, content.value, props.currentPolicy.policyNumber);

  return content.value;
}

return (_ctx: any,_cache: any) => {
  return (policySummary.value)
    ? (_openBlock(), _createBlock(SimpleReviewSummary, {
        key: 0,
        title: policySummary.value.title,
        icon: policySummary.value.icon
      }, {
        reviewSummaryItems: _withCtx(() => [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(policySummary.value?.items, (item, itemIndex) => {
            return (_openBlock(), _createBlock(ReviewSummaryItem, {
              key: itemIndex,
              item: item,
              isLastReviewSummaryItem: !((itemIndex as number) < policySummary.value.items.length - 1)
            }, {
              title: _withCtx(() => [
                _createElementVNode("div", _hoisted_1, [
                  _createElementVNode("div", {
                    class: "text-racq-dark-grey text-base-bold",
                    innerHTML: item.fields.title.value
                  }, null, 8, _hoisted_2)
                ])
              ]),
              content: _withCtx(() => [
                _createElementVNode("div", {
                  class: "text-base",
                  innerHTML: fillBookmarks(item.fields.content)
                }, null, 8, _hoisted_3)
              ]),
              secondaryLinks: _withCtx(() => [
                (item.fields?.firstLink.value.class || item.fields?.secondLink.value.class)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                      (item.fields?.firstLink.value.class === 'CertificateOfInsurance')
                        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                            _createVNode(DocumentLink, {
                              enabled: _ctx.currentPolicy.currentDocumentId?.length > 0,
                              linkText: item.fields?.firstLink.value.text,
                              enabledIcon: require('@/assets/icons/document-icon-blue.svg'),
                              disabledIcon: require('@/assets/icons/document-icon-grey.svg'),
                              dataTestId: "btnCertificateOfInsurance",
                              onLinkClicked: _cache[0] || (_cache[0] = () => downloadDocument(_ctx.currentPolicy?.currentDocumentId))
                            }, null, 8, ["enabled", "linkText", "enabledIcon", "disabledIcon"])
                          ]))
                        : _createCommentVNode("", true),
                      (
                item.fields?.secondLink.value.class === 'CertificateOfCurrency' &&
                _ctx.currentPolicy.isCertOfCurrencyGenAllowed
              )
                        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                            _createVNode(DocumentLink, {
                              enabled: true,
                              linkText: item.fields?.secondLink.value.text,
                              enabledIcon: require('@/assets/icons/document-icon-blue.svg'),
                              disabledIcon: require('@/assets/icons/document-icon-grey.svg'),
                              dataTestId: "btnCertificateOfCurrency",
                              onLinkClicked: _cache[1] || (_cache[1] = () => requestCoc(_ctx.currentPolicy?.policyNumber))
                            }, null, 8, ["linkText", "enabledIcon", "disabledIcon"])
                          ]))
                        : _createCommentVNode("", true)
                    ]))
                  : _createCommentVNode("", true),
                (
              (item.fields?.firstLink.value.class || item.fields?.secondLink.value.class) &&
              certificateDownloadResponse.value?.length > 0
            )
                  ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                      (certificateDownloadResponse.value === 'coi-error')
                        ? (_openBlock(), _createBlock(InformationBox, {
                            key: 0,
                            description: certificateOfInsuranceDownloadError.value.content.value,
                            type: _unref(InformationBoxTypes).ERROR
                          }, null, 8, ["description", "type"]))
                        : _createCommentVNode("", true),
                      (certificateDownloadResponse.value === 'coc-error')
                        ? (_openBlock(), _createBlock(InformationBox, {
                            key: 1,
                            description: certificateOfCurrencyDownloadError.value.content.value,
                            type: _unref(InformationBoxTypes).ERROR
                          }, null, 8, ["description", "type"]))
                        : _createCommentVNode("", true)
                    ]))
                  : _createCommentVNode("", true)
              ]),
              edit: _withCtx(() => _cache[2] || (_cache[2] = [])),
              _: 2
            }, 1032, ["item", "isLastReviewSummaryItem"]))
          }), 128))
        ]),
        _: 1
      }, 8, ["title", "icon"]))
    : _createCommentVNode("", true)
}
}

})